import React, { useState, useEffect, useCallback } from 'react';
import {
  TextField,
  Button,
  Typography,
  Container,
  Grid,
  Paper,
  CircularProgress,
  Box,
  IconButton,
  Alert,
  Divider,
  Chip,
  ClickAwayListener,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { AddCircleOutline, DeleteOutline, ColorLens as ColorLensIcon } from '@mui/icons-material';
import { ChromePicker } from 'react-color';
import { toast } from 'react-toastify';
import { getSectionContent, updateSectionContent } from '../services/contentService';

const commonFontFamilies = [
  'Arial', 'Verdana', 'Helvetica', 'Tahoma', 'Trebuchet MS', 'Times New Roman', 'Georgia', 'Garamond',
  'Courier New', 'Brush Script MT', 'Calibri', 'Candara', 'Optima', 'Gill Sans', 'Futura', 'Avant Garde',
  'Palatino', 'Bookman', 'Lucida Console', 'Monaco', 'Consolas', 'Courier', 'Lucida Handwriting', 'Impact', 'Comic Sans MS'
];

const fontSizeOptions = [
  { value: 12, label: '12px' },
  { value: 14, label: '14px' },
  { value: 16, label: '16px' },
  { value: 18, label: '18px' },
  { value: 20, label: '20px' },
  { value: 24, label: '24px' },
  { value: 30, label: '30px' },
  { value: 36, label: '36px' },
  { value: 48, label: '48px' },
  { value: 60, label: '60px' },
  { value: 72, label: '72px' },
  { value: 96, label: '96px' },
];

const fontWeightOptions = [
  { value: 'font-thin', label: 'Thin (100)' },
  { value: 'font-extralight', label: 'Extra Light (200)' },
  { value: 'font-light', label: 'Light (300)' },
  { value: 'font-normal', label: 'Normal (400)' },
  { value: 'font-medium', label: 'Medium (500)' },
  { value: 'font-semibold', label: 'Semibold (600)' },
  { value: 'font-bold', label: 'Bold (700)' },
  { value: 'font-extrabold', label: 'Extra Bold (800)' },
  { value: 'font-black', label: 'Black (900)' },
];

const defaultContactUsData = {
  mainTitle: 'Contact Us',
  mainTitleColor: '#1E40AF',
  mainTitleFontSize: 36, // Default numeric value, matching frontend
  mainTitleFontFamily: 'Arial',
  mainTitleFontWeight: 'font-semibold',
  sectionBackgroundColor: '#F9FAFB',

  detailsBoxTitle: 'Get in Touch Directly',
  detailsBoxTitleColor: '#FFFFFF',
  detailsBoxTitleFontSize: 24, // Default numeric value, matching frontend
  detailsBoxTitleFontFamily: 'Arial',
  detailsBoxTitleFontWeight: 'font-semibold',
  detailsBoxBackground: '#2563EB',

  address: '123 Fuel Street, Auto Nagar, Anytown, India - 123456',
  emails: ['info@fuelsolution.com', 'sales@fuelsolution.com'],
  phones: ['+919876543210', '+919123456789', '+919000000000'],
  contactInfoColor: '#E5E7EB',
  contactIconColor: '#FFFFFF',
  contactInfoFontSize: 16, // Added default numeric value
  contactInfoFontWeight: 'font-normal', // Added default font weight
  contactInfoFontFamily: 'Arial', // Added default font family

  socialBoxTitle: 'Connect With Us',
  socialBoxTitleColor: '#FFFFFF',
  socialBoxTitleFontSize: 18, // Default numeric value, matching frontend
  socialBoxTitleFontFamily: 'Arial',
  socialBoxTitleFontWeight: 'font-semibold', // Added default font weight
  instagramUrl: 'https://instagram.com',
  facebookUrl: 'https://facebook.com',
  whatsappNumber: '919876543210',
  socialIconColor: '#2563EB',
  socialIconBackgroundColor: '#FFFFFF',

  mapEmbedUrl: '',
};

const ContactUsSectionEditor = () => {
  // Helper to get nested values safely
  const getNestedValue = (obj, path, defaultValue = '') => {
    const value = path.split('.').reduce((acc, part) => acc && acc[part], obj);
    return value === undefined || value === null ? defaultValue : value;
  };

  const [sectionData, setSectionData] = useState(defaultContactUsData);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Color Picker State
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [currentColorField, setCurrentColorField] = useState(null);
  const [colorPickerAnchorEl, setColorPickerAnchorEl] = useState(null);

  // For emails and phones input
  const [currentEmail, setCurrentEmail] = useState('');
  const [currentPhone, setCurrentPhone] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getSectionContent('contactUsSection');
      let processedData = {
        ...defaultContactUsData,
        ...(data || {}),
        emails: (data && data.emails) ? data.emails : defaultContactUsData.emails,
        phones: (data && data.phones) ? data.phones : defaultContactUsData.phones,
      };

      const fontSizeKeys = ['mainTitleFontSize', 'detailsBoxTitleFontSize', 'socialBoxTitleFontSize', 'contactInfoFontSize'];
      fontSizeKeys.forEach(key => {
        const defaultValue = defaultContactUsData[key];
        let val = processedData[key];
        // If value is not a number or not one of our valid options, use default
        if (typeof val !== 'number' || !fontSizeOptions.some(option => option.value === val)) {
          processedData[key] = defaultValue;
        }
      });

      // Ensure font weight values are strings and valid
      const fontWeightKeys = ['mainTitleFontWeight', 'detailsBoxTitleFontWeight', 'socialBoxTitleFontWeight', 'contactInfoFontWeight'];
      fontWeightKeys.forEach(key => {
        const defaultValue = defaultContactUsData[key];
        let val = processedData[key];
        if (typeof val !== 'string' || !fontWeightOptions.some(option => option.value === val)) {
          processedData[key] = defaultValue;
        }
      });

      const fontFamilyKeys = ['mainTitleFontFamily', 'detailsBoxTitleFontFamily', 'socialBoxTitleFontFamily', 'contactInfoFontFamily'];
      fontFamilyKeys.forEach(key => {
        const defaultValue = defaultContactUsData[key];
        if (!processedData[key] || typeof processedData[key] !== 'string' || !commonFontFamilies.includes(processedData[key])) {
          processedData[key] = defaultValue;
        }
      });

      setSectionData(processedData);
    } catch (err) {
      setError(err.message || 'Failed to fetch Contact Us content. Displaying default data.');
      toast.error(err.message || 'Failed to fetch Contact Us content.');
      setSectionData(defaultContactUsData);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleInputChange = (event, path) => {
    const { name, value: rawValue, type } = event.target;
    let value = rawValue;

    // Process font size fields as numbers
    if (name.endsWith('FontSize')) {
      if (rawValue === '' || rawValue === null) {
        value = ''; // Allow temporarily empty for better UX
      } else {
        const parsed = parseFloat(rawValue);
        value = isNaN(parsed) ? rawValue : parsed; // Keep as raw string if not a valid number, or let validation handle it
      }
    }

    if (path) {
      const keys = path.split('.');
      setSectionData(prevData => {
        let updatedData = { ...prevData };
        let currentLevel = updatedData;
        for (let i = 0; i < keys.length - 1; i++) {
          currentLevel[keys[i]] = { ...(currentLevel[keys[i]] || {}) };
          currentLevel = currentLevel[keys[i]];
        }
        currentLevel[keys[keys.length - 1]] = value;
        return updatedData;
      });
    } else {
      setSectionData(prevData => ({ ...prevData, [name]: value }));
    }
  };

  const handleColorPickerOpen = (event, fieldName) => {
    setColorPickerAnchorEl(event.currentTarget);
    setShowColorPicker(true);
    setCurrentColorField(fieldName);
  };

  const handleColorChange = (color, fieldName) => {
    if (fieldName) {
      setSectionData(prevData => {
        const keys = fieldName.split('.');
        let updatedData = { ...prevData };
        let currentLevel = updatedData;
        for (let i = 0; i < keys.length - 1; i++) {
          currentLevel[keys[i]] = { ...(currentLevel[keys[i]] || {}) };
          currentLevel = currentLevel[keys[i]];
        }
        currentLevel[keys[keys.length - 1]] = color.hex;
        return updatedData;
      });
    }
  };

  const handleColorPickerClose = () => {
    setShowColorPicker(false);
    setCurrentColorField(null);
    setColorPickerAnchorEl(null);
  };

  const handleAddItem = (type) => {
    if (type === 'email' && currentEmail.trim() !== '') {
      setSectionData(prevData => ({
        ...prevData,
        emails: [...(prevData.emails || []), currentEmail.trim()],
      }));
      setCurrentEmail('');
    } else if (type === 'phone' && currentPhone.trim() !== '') {
      setSectionData(prevData => ({
        ...prevData,
        phones: [...(prevData.phones || []), currentPhone.trim()],
      }));
      setCurrentPhone('');
    }
  };

  const handleDeleteItem = (type, itemToDelete) => {
    if (type === 'email') {
      setSectionData(prevData => ({
        ...prevData,
        emails: prevData.emails.filter(item => item !== itemToDelete),
      }));
    } else if (type === 'phone') {
      setSectionData(prevData => ({
        ...prevData,
        phones: prevData.phones.filter(item => item !== itemToDelete),
      }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await updateSectionContent('contactUsSection', sectionData);
      toast.success('Contact Us content updated successfully!');
      fetchData(); // Re-fetch to confirm and get latest
    } catch (err) {
      setError(err.message || 'Failed to save Contact Us content.');
      toast.error(err.message || 'Failed to save Contact Us content.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}><CircularProgress /></Container>;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: { xs: 2, md: 4 } }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 3, textAlign: 'center', color: 'primary.main' }}>
          Contact Us Section Editor
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* General Section Styling */}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 2, fontWeight: 'medium', color: 'primary.main' }}>Overall Section Settings</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}><TextField label="Main Title Text" name="mainTitle" value={getNestedValue(sectionData, 'mainTitle')} onChange={handleInputChange} fullWidth /></Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                <TextField label="Main Title Color" name="mainTitleColor" value={getNestedValue(sectionData, 'mainTitleColor')} onChange={handleInputChange} fullWidth sx={{ flexGrow: 1 }} />
                <IconButton onClick={(e) => handleColorPickerOpen(e, 'mainTitleColor')}><ColorLensIcon /></IconButton>
              </Box>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel id="mainTitleFontSize-label">Font Size</InputLabel>
                <Select
                  labelId="mainTitleFontSize-label"
                  name="mainTitleFontSize"
                  value={getNestedValue(sectionData, 'mainTitleFontSize', defaultContactUsData.mainTitleFontSize)}
                  label="Font Size"
                  onChange={(e) => handleInputChange(e, 'mainTitleFontSize')}
                >
                  {fontSizeOptions.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel id="mainTitleFontWeight-label">Font Weight</InputLabel>
                <Select
                  labelId="mainTitleFontWeight-label"
                  name="mainTitleFontWeight"
                  value={getNestedValue(sectionData, 'mainTitleFontWeight', defaultContactUsData.mainTitleFontWeight)}
                  label="Font Weight"
                  onChange={(e) => handleInputChange(e, 'mainTitleFontWeight')}
                >
                  {fontWeightOptions.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel id="mainTitleFontFamily-label">Font Family</InputLabel>
                <Select
                  labelId="mainTitleFontFamily-label"
                  name="mainTitleFontFamily"
                  value={getNestedValue(sectionData, 'mainTitleFontFamily', defaultContactUsData.mainTitleFontFamily)}
                  label="Font Family"
                  onChange={(e) => handleInputChange(e, 'mainTitleFontFamily')}
                >
                  {commonFontFamilies.map(font => <MenuItem key={font} value={font}>{font}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                <TextField label="Section Background Color" name="sectionBackgroundColor" value={getNestedValue(sectionData, 'sectionBackgroundColor', '#FFFFFF')} onChange={handleInputChange} fullWidth sx={{ flexGrow: 1 }} />
                <IconButton onClick={(e) => handleColorPickerOpen(e, 'sectionBackgroundColor')}><ColorLensIcon /></IconButton>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 2, fontWeight: 'medium', color: 'primary.main' }}>"Get in Touch Directly" Box</Typography>
          <Grid container spacing={3} sx={{ mb: 2 }}>
            <Grid item xs={12}><TextField label="Box Title Text" name="detailsBoxTitle" value={getNestedValue(sectionData, 'detailsBoxTitle')} onChange={handleInputChange} fullWidth /></Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                <TextField label="Box Title Color" name="detailsBoxTitleColor" value={getNestedValue(sectionData, 'detailsBoxTitleColor')} onChange={handleInputChange} fullWidth sx={{ flexGrow: 1 }} />
                <IconButton onClick={(e) => handleColorPickerOpen(e, 'detailsBoxTitleColor')}><ColorLensIcon /></IconButton>
              </Box>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel id="detailsBoxTitleFontSize-label">Font Size</InputLabel>
                <Select
                  labelId="detailsBoxTitleFontSize-label"
                  name="detailsBoxTitleFontSize"
                  value={getNestedValue(sectionData, 'detailsBoxTitleFontSize', defaultContactUsData.detailsBoxTitleFontSize)}
                  label="Font Size"
                  onChange={(e) => handleInputChange(e, 'detailsBoxTitleFontSize')}
                >
                  {fontSizeOptions.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel id="detailsBoxTitleFontWeight-label">Font Weight</InputLabel>
                <Select
                  labelId="detailsBoxTitleFontWeight-label"
                  name="detailsBoxTitleFontWeight"
                  value={getNestedValue(sectionData, 'detailsBoxTitleFontWeight', defaultContactUsData.detailsBoxTitleFontWeight)}
                  label="Font Weight"
                  onChange={(e) => handleInputChange(e, 'detailsBoxTitleFontWeight')}
                >
                  {fontWeightOptions.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel id="detailsBoxTitleFontFamily-label">Font Family</InputLabel>
                <Select
                  labelId="detailsBoxTitleFontFamily-label"
                  name="detailsBoxTitleFontFamily"
                  value={getNestedValue(sectionData, 'detailsBoxTitleFontFamily', defaultContactUsData.detailsBoxTitleFontFamily)}
                  label="Font Family"
                  onChange={(e) => handleInputChange(e, 'detailsBoxTitleFontFamily')}
                >
                  {commonFontFamilies.map(font => <MenuItem key={font} value={font}>{font}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                <TextField label="Box Background Color" name="detailsBoxBackground" value={getNestedValue(sectionData, 'detailsBoxBackground')} onChange={handleInputChange} fullWidth sx={{ flexGrow: 1 }} />
                <IconButton onClick={(e) => handleColorPickerOpen(e, 'detailsBoxBackground')}><ColorLensIcon /></IconButton>
              </Box>
            </Grid>
            <Grid item xs={12}><TextField label="Address" name="address" value={getNestedValue(sectionData, 'address')} onChange={handleInputChange} fullWidth multiline rows={2} /></Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                <TextField label="Contact Info Text Color" name="contactInfoColor" value={getNestedValue(sectionData, 'contactInfoColor')} onChange={handleInputChange} fullWidth sx={{ flexGrow: 1 }} />
                <IconButton onClick={(e) => handleColorPickerOpen(e, 'contactInfoColor')}><ColorLensIcon /></IconButton>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                <TextField label="Contact Info Icon Color" name="contactIconColor" value={getNestedValue(sectionData, 'contactIconColor')} onChange={handleInputChange} fullWidth sx={{ flexGrow: 1 }} />
                <IconButton onClick={(e) => handleColorPickerOpen(e, 'contactIconColor')}><ColorLensIcon /></IconButton>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel id="contactInfoFontSize-label">Font Size</InputLabel>
                <Select
                  labelId="contactInfoFontSize-label"
                  name="contactInfoFontSize"
                  value={getNestedValue(sectionData, 'contactInfoFontSize', defaultContactUsData.contactInfoFontSize)}
                  label="Font Size"
                  onChange={(e) => handleInputChange(e, 'contactInfoFontSize')}
                >
                  {fontSizeOptions.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel id="contactInfoFontWeight-label">Font Weight</InputLabel>
                <Select
                  labelId="contactInfoFontWeight-label"
                  name="contactInfoFontWeight"
                  value={getNestedValue(sectionData, 'contactInfoFontWeight', defaultContactUsData.contactInfoFontWeight)}
                  label="Font Weight"
                  onChange={(e) => handleInputChange(e, 'contactInfoFontWeight')}
                >
                  {fontWeightOptions.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel id="contactInfoFontFamily-label">Font Family</InputLabel>
                <Select
                  labelId="contactInfoFontFamily-label"
                  name="contactInfoFontFamily"
                  value={getNestedValue(sectionData, 'contactInfoFontFamily', defaultContactUsData.contactInfoFontFamily)}
                  label="Font Family"
                  onChange={(e) => handleInputChange(e, 'contactInfoFontFamily')}
                >
                  {commonFontFamilies.map(font => <MenuItem key={font} value={font}>{font}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Typography variant="subtitle1" gutterBottom>Email Addresses</Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 1}}>
              <TextField label="New Email" value={currentEmail} onChange={(e) => setCurrentEmail(e.target.value)} size="small" sx={{flexGrow: 1}}/>
              <Button variant="outlined" onClick={() => handleAddItem('email')} startIcon={<AddCircleOutline />}>Add</Button>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {sectionData.emails?.map(email => <Chip key={email} label={email} onDelete={() => handleDeleteItem('email', email)} />)}
          </Box>
          <Typography variant="subtitle1" gutterBottom>Phone Numbers</Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 1}}>
              <TextField label="New Phone" value={currentPhone} onChange={(e) => setCurrentPhone(e.target.value)} size="small" sx={{flexGrow: 1}}/>
              <Button variant="outlined" onClick={() => handleAddItem('phone')} startIcon={<AddCircleOutline />}>Add</Button>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {sectionData.phones?.map(phone => <Chip key={phone} label={phone} onDelete={() => handleDeleteItem('phone', phone)} />)}
          </Box>
        </Paper>

        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 2, fontWeight: 'medium', color: 'primary.main' }}>"Connect With Us" Box</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField label="Box Title Text" name="socialBoxTitle" value={getNestedValue(sectionData, 'socialBoxTitle')} onChange={handleInputChange} fullWidth />
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                  <TextField label="Box Title Color" name="socialBoxTitleColor" value={getNestedValue(sectionData, 'socialBoxTitleColor')} onChange={handleInputChange} fullWidth sx={{ flexGrow: 1 }} />
                  <IconButton onClick={(e) => handleColorPickerOpen(e, 'socialBoxTitleColor')}><ColorLensIcon /></IconButton>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="socialBoxTitleFontSize-label">Font Size</InputLabel>
                  <Select
                    labelId="socialBoxTitleFontSize-label"
                    name="socialBoxTitleFontSize"
                    value={getNestedValue(sectionData, 'socialBoxTitleFontSize', defaultContactUsData.socialBoxTitleFontSize)}
                    label="Font Size"
                    onChange={(e) => handleInputChange(e, 'socialBoxTitleFontSize')}
                  >
                    {fontSizeOptions.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="socialBoxTitleFontWeight-label">Font Weight</InputLabel>
                  <Select
                    labelId="socialBoxTitleFontWeight-label"
                    name="socialBoxTitleFontWeight"
                    value={getNestedValue(sectionData, 'socialBoxTitleFontWeight', defaultContactUsData.socialBoxTitleFontWeight)}
                    label="Font Weight"
                    onChange={(e) => handleInputChange(e, 'socialBoxTitleFontWeight')}
                  >
                    {fontWeightOptions.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="socialBoxTitleFontFamily-label">Font Family</InputLabel>
                  <Select
                    labelId="socialBoxTitleFontFamily-label"
                    name="socialBoxTitleFontFamily"
                    value={getNestedValue(sectionData, 'socialBoxTitleFontFamily', defaultContactUsData.socialBoxTitleFontFamily)}
                    label="Font Family"
                    onChange={(e) => handleInputChange(e, 'socialBoxTitleFontFamily')}
                  >
                    {commonFontFamilies.map(font => <MenuItem key={font} value={font}>{font}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                  <TextField label="Social Icon Color" name="socialIconColor" value={getNestedValue(sectionData, 'socialIconColor')} onChange={handleInputChange} fullWidth sx={{ flexGrow: 1 }} />
                  <IconButton onClick={(e) => handleColorPickerOpen(e, 'socialIconColor')}><ColorLensIcon /></IconButton>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                  <TextField label="Social Icon Background Color" name="socialIconBackgroundColor" value={getNestedValue(sectionData, 'socialIconBackgroundColor')} onChange={handleInputChange} fullWidth sx={{ flexGrow: 1 }} />
                  <IconButton onClick={(e) => handleColorPickerOpen(e, 'socialIconBackgroundColor')}><ColorLensIcon /></IconButton>
                </Box>
              </Grid>
              <Grid item xs={12}><TextField label="Instagram URL" name="instagramUrl" value={getNestedValue(sectionData, 'instagramUrl')} onChange={handleInputChange} fullWidth /></Grid>
              <Grid item xs={12}><TextField label="Facebook URL" name="facebookUrl" value={getNestedValue(sectionData, 'facebookUrl')} onChange={handleInputChange} fullWidth /></Grid>
              <Grid item xs={12}><TextField label="WhatsApp Number" name="whatsappNumber" value={getNestedValue(sectionData, 'whatsappNumber')} onChange={handleInputChange} fullWidth /></Grid>
            </Grid>
        </Paper>

        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 2, fontWeight: 'medium', color: 'primary.main' }}>Map Section</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label="Map Embed URL"
                name="mapEmbedUrl"
                value={getNestedValue(sectionData, 'mapEmbedUrl')}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={4}
                helperText="Paste the embed URL for your Google Map here (e.g., from Google Maps 'Share' -> 'Embed a map')"
              />
            </Grid>
          </Grid>
        </Paper>

        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving}
          sx={{ mt: 3, py: 1.5, fontSize: '1.1rem' }}
          fullWidth
        >
          {saving ? <CircularProgress size={24} color="inherit" /> : 'Save Contact Us Section'}
        </Button>

        {showColorPicker && (currentColorField) && (
          <ClickAwayListener onClickAway={handleColorPickerClose}>
            <Box sx={{
              position: 'absolute',
              zIndex: 2,
              top: colorPickerAnchorEl ? colorPickerAnchorEl.getBoundingClientRect().bottom + window.scrollY + 10 : 0,
              left: colorPickerAnchorEl ? colorPickerAnchorEl.getBoundingClientRect().left + window.scrollX : 0,
            }}>
              <ChromePicker
                color={getNestedValue(sectionData, currentColorField)}
                onChangeComplete={(color) => handleColorChange(color, currentColorField)}
              />
            </Box>
          </ClickAwayListener>
        )}
      </Paper>
    </Container>
  );
};

export default ContactUsSectionEditor;